import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { SERVICES } from '../content/site';
import { PageHero, SectionHeading } from '../components/shared/PageHero';
import { Reveal } from '../components/shared/Reveal';
import { CtaBand } from '../components/home/Sections';
import { Button, Card } from '../components/sv';

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      <PageHero kicker="Services" title="Ocean, bulk and inland. One line, one plan." text="From a single container to a Capesize parcel, every service can be quoted, booked, paid and tracked online, backed by local teams in 121 countries." image="/img/sudvik-vessel.jpg" crumbs={[{ label: "Services" }]}>
        <Button to="/quote" data-testid="services-quote-btn">Get a quote</Button>
        <Button variant="secondary" to="/contact" data-testid="services-contact-btn">Talk to sales</Button>
      </PageHero>
      <section className="py-20 md:py-24">
        <div className="sv-wrap grid gap-6">
          {SERVICES.map(function (s) {
            return (
              <Reveal key={s.slug}>
                <Link to={'/services/' + s.slug} className="sv-card grid lg:grid-cols-12 overflow-hidden hover:border-ink transition-colors duration-[120ms]" data-testid={"service-row-" + s.slug}>
                  <div className="lg:col-span-5 min-h-[220px] bg-gray-200"><img src={s.hero} alt="" className="w-full h-full object-cover" /></div>
                  <div className="lg:col-span-7 p-8 lg:p-10">
                    <s.icon className="h-6 w-6 text-ink" strokeWidth={1.75} />
                    <h2 className="text-[28px] md:text-[32px] sv-display mt-4">{s.title}</h2>
                    <p className="text-gray-700 mt-3">{s.description}</p>
                    <div className="flex flex-wrap gap-8 mt-6">{s.stats.map(function (st) { return <div key={st[1]}><div className="text-2xl font-light tracking-[-0.01em]">{st[0]}</div><div className="text-sm text-gray-600">{st[1]}</div></div>; })}</div>
                    <span className="sv-ink-link mt-6">Learn more <ArrowRight className="h-[18px] w-[18px]" strokeWidth={1.75} /></span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>
      <CtaBand />
    </div>
  );
}

export function ServiceDetailPage() {
  const { slug } = useParams();
  const s = SERVICES.find(function (x) { return x.slug === slug; });
  if (!s) return <Navigate to="/services" replace />;
  const isBulk = slug === 'bulk-shipping';
  const others = SERVICES.filter(function (x) { return x.slug !== slug; }).slice(0, 3);
  return (
    <div data-testid={"service-detail-" + slug}>
      <PageHero kicker="Services" title={s.title} text={s.short} image={s.hero} crumbs={[{ label: 'Services', href: '/services' }, { label: s.title }]}>
        <Button to={isBulk ? '/quote?mode=bulk' : '/quote'} data-testid="service-quote-btn">{isBulk ? 'Get bulk prices' : 'Get a quote'}</Button>
        <Button variant="secondary" to={isBulk ? '/booking?mode=bulk' : '/booking'} data-testid="service-book-btn">Book cargo</Button>
      </PageHero>
      <section className="py-20 md:py-24">
        <div className="sv-wrap grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <SectionHeading kicker="Overview" title={"How " + s.title.toLowerCase() + " works"} text={s.description} />
            <ul className="grid sm:grid-cols-2 gap-3">
              {s.features.map(function (f) { return <li key={f} className="flex gap-3 p-4 bg-gray-50 border border-gray-200 rounded text-[15px]"><Check className="h-5 w-5 text-blue-600 shrink-0" strokeWidth={1.75} />{f}</li>; })}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="sv-card-inverse p-8 sticky top-24">
              <p className="text-sm text-white/70">Key figures</p>
              <div className="mt-4 divide-y divide-white/10">{s.stats.map(function (st) { return <div key={st[1]} className="py-4 flex items-baseline justify-between gap-4"><span className="text-4xl font-light tracking-[-0.015em]">{st[0]}</span><span className="text-sm text-white/70">{st[1]}</span></div>; })}</div>
              {isBulk && <BulkRatesTable />}
              <Button variant="inverse" className="w-full mt-8" to="/contact" data-testid="service-contact-btn">Speak to a specialist</Button>
            </div>
          </div>
        </div>
      </section>
      {isBulk && <BulkProcess />}
      <section className="py-20 bg-gray-50 border-t border-gray-200">
        <div className="sv-wrap">
          <SectionHeading kicker="Related" title="Other services" />
          <div className="grid md:grid-cols-3 gap-6">
            {others.map(function (o) { return <Link key={o.slug} to={'/services/' + o.slug} className="sv-card p-6 block hover:border-ink transition-colors duration-[120ms]"><o.icon className="h-6 w-6 text-ink" strokeWidth={1.75} /><div className="text-[22px] tracking-[-0.01em] mt-4">{o.title}</div><p className="text-gray-700 mt-2">{o.short}</p></Link>; })}
          </div>
        </div>
      </section>
    </div>
  );
}

function BulkRatesTable() {
  const rows = [['Grain', 38], ['Coal', 22], ['Iron ore', 18], ['Cement', 30], ['Fertiliser', 35], ['Steel products', 55], ['Project cargo', 85]];
  return (
    <div className="mt-6">
      <p className="text-sm text-white/70 mb-2">Indicative freight, USD per metric ton</p>
      <table className="w-full text-sm" data-testid="bulk-rates-table"><tbody>{rows.map(function (r) { return <tr key={r[0]} className="border-t border-white/10"><td className="py-2">{r[0]}</td><td className="py-2 text-right sv-mono">{r[1].toFixed(2)}</td></tr>; })}</tbody></table>
    </div>
  );
}

function BulkProcess() {
  const steps = [['Nominate cargo', 'Choose dry or break-bulk, the commodity and the parcel size (minimum 500 MT).'], ['Fix tonnage', 'We suggest Handysize to Capesize tonnage and publish a per-ton rate with your loading terms.'], ['Load and sail', 'Stevedoring, trimming and draft survey are handled at berth; the vessel departs on the agreed laycan.'], ['Discharge and settle', 'Statement of facts, laytime calculation and final freight invoice are delivered to your portal.']];
  return (
    <section className="py-20 md:py-24 bg-gray-50 border-t border-gray-200" data-testid="bulk-process">
      <div className="sv-wrap">
        <SectionHeading kicker="Bulk booking process" title="Four steps from nomination to discharge" />
        <div className="grid md:grid-cols-4 gap-6">
          {steps.map(function (st, i) { return <Card key={st[0]} padding="p-6"><div className="sv-mono text-sm text-gray-600">0{i + 1}</div><h3 className="text-[22px] font-normal tracking-[-0.01em] mt-3">{st[0]}</h3><p className="text-gray-700 text-[15px] mt-2">{st[1]}</p></Card>; })}
        </div>
      </div>
    </section>
  );
}
